import React from 'react';

import {
    SelectTextField,
    type SingleSelectTextFieldProps,
    type MultipleSelectTextFieldProps,
} from '@lumapps/combobox/components/SelectTextField';
import { ComboboxOptionProps } from '@lumapps/combobox/types';
import { getGroupPublic, getGroupAll } from '@lumapps/customer/ducks/selectors';
import { useDataAttributes } from '@lumapps/data-attributes';
import { Group } from '@lumapps/groups/types';
import { mdiAccountMultiple, mdiAlertCircle, mdiEarth } from '@lumapps/lumx/icons';
import { Chip, ColorPalette, Icon, Size } from '@lumapps/lumx/react';
import { useSelector } from '@lumapps/redux/react';
import { SEGMENT_IN_GROUP_PICKER_IDENTIFIER } from '@lumapps/segment/constants';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { USER } from '@lumapps/user/keys';

import { useGroupPickerFieldState, UseGroupPickerFieldStateOptions } from '../../hooks/useGroupPickerFieldState';
import { GroupMoreInfo } from './GroupMoreInfo';
import { SegmentMoreInfo } from './SegmentMoreInfo';

export interface BaseGroupPickerFieldProps extends Omit<UseGroupPickerFieldStateOptions, 'value'> {
    label?: string;
    helper?: React.ReactNode;
    /** additional props for each of the group options */
    getOptionProps?: (group: Group) => Partial<ComboboxOptionProps<Group>>;
}

export type SingleGroupPickerFieldProps = {
    selectionType: SingleSelectTextFieldProps['selectionType'];
    /** custom classname */
    className?: SingleSelectTextFieldProps['className'];
    /** Currently selected instance */
    value?: Group | string;
    /** Callback on selection change */
    onChange?: SingleSelectTextFieldProps<Group>['onChange'];
    /** Additional props for the select field component */
    selectTextFieldProps?: Partial<Omit<SingleSelectTextFieldProps<Group>, 'selectionType' | 'value' | 'onChange'>>;
};

export type MultipleGroupPickerFieldProps = {
    selectionType: MultipleSelectTextFieldProps['selectionType'];
    /** custom classname */
    className?: MultipleSelectTextFieldProps['className'];
    /** Currently selected instance */
    value?: Array<Group | string>;
    /** Callback on selection change */
    onChange?: MultipleSelectTextFieldProps<Group>['onChange'];
    /** Additional props for the select field component */
    selectTextFieldProps?: Partial<Omit<MultipleSelectTextFieldProps<Group>, 'selectionType' | 'value' | 'onChange'>>;
};

export type GroupPickerFieldProps = BaseGroupPickerFieldProps &
    (SingleGroupPickerFieldProps | MultipleGroupPickerFieldProps);

const SCOPE = 'group-picker-field';

/**
 * Select (single or multiple) Group picker field
 *
 * @family Pickers
 * @param GroupPickerFieldProps
 * @returns GroupPickerField
 */
export const GroupPickerField: React.FC<GroupPickerFieldProps> = ({
    selectionType,
    value,
    onChange,
    className,
    selectTextFieldProps,
    shouldSearchInPlatform = false,
    shouldDisplayPublicGroup = true,
    shouldDisplayAllGroup = true,
    shouldDisplaySegments = false,
    searchParams,
    label,
    helper,
    getOptionProps,
}) => {
    const allGroup = useSelector(getGroupAll);
    const publicGroup = useSelector(getGroupPublic);
    const scope = selectTextFieldProps?.scope || SCOPE;
    const { get } = useDataAttributes(scope);
    const { translateKey } = useTranslate();
    const shouldPickerDisplaySegments = shouldDisplaySegments || searchParams?.excludeSegments === false;

    const {
        setSearchValue,
        setEnabled,
        isDisabled,
        groups,
        singleSelectedValue,
        multipleSelectedValue,
        status,
        fetchNextPage,
    } = useGroupPickerFieldState({
        value,
        shouldSearchInPlatform,
        shouldDisplayPublicGroup,
        shouldDisplayAllGroup,
        shouldDisplaySegments: shouldPickerDisplaySegments,
        searchParams,
    });

    // Default value for renderChip, used when selectTextFieldProps does not define one
    const renderChip = (group: Group) => {
        // If group name is undefined, the group is unavailable (= deleted or not synced yet)
        if (!group.name) {
            const errorIcon = <Icon icon={mdiAlertCircle} size={Size.xxs} color={ColorPalette.yellow} />;
            return (
                <Chip before={errorIcon} color={ColorPalette.yellow}>
                    {translateKey(GLOBAL.UNAVAILABLE_GROUP)}
                </Chip>
            );
        }
        return translateKey(group.name);
    };

    const selectionProps =
        selectionType === 'single'
            ? {
                  selectionType,
                  value: singleSelectedValue,
                  onChange,
                  ...selectTextFieldProps,
              }
            : {
                  selectionType,
                  value: multipleSelectedValue,
                  onChange,
                  renderChip,
                  ...selectTextFieldProps,
              };

    const renderOptionFromProps = selectTextFieldProps?.renderOption;

    return (
        <SelectTextField<Group>
            autoFilter={false}
            getOptionId="id"
            className={className}
            getOptionName={(group: Group) => translateKey(group.name)}
            getSectionId={(group: Group) => {
                if (!shouldDisplayAllGroup && !shouldDisplayPublicGroup) {
                    return undefined;
                }

                if (group.id === allGroup?.id || group.id === publicGroup?.id) {
                    return 'public';
                }

                return 'other';
            }}
            renderSectionTitle={(group: Group) => {
                if (!shouldDisplayAllGroup && !shouldDisplayPublicGroup) {
                    return undefined;
                }

                if (group.id === allGroup?.id || group.id === publicGroup?.id) {
                    return (
                        <>
                            <Icon icon={mdiEarth} size={Size.xs} />
                            {translateKey(USER.FEED_GROUP_TECHNICAL)}
                        </>
                    );
                }

                return (
                    <>
                        <Icon icon={mdiAccountMultiple} size={Size.xs} />
                        {translateKey(shouldSearchInPlatform ? USER.CUSTOMER_FEEDS : GLOBAL.FEEDS)}
                    </>
                );
            }}
            label={label || translateKey(GLOBAL.ADD_GROUPS)}
            helper={helper}
            scope={scope}
            {...selectionProps}
            renderOption={(group: Group, index: number) => {
                if (renderOptionFromProps) {
                    return renderOptionFromProps(group, index);
                }

                const additionalOptionProps = getOptionProps?.(group) || {};

                const { functionalInnerId: id } = group;
                const dataIds = get({ element: 'option', action: group.id });
                if (shouldPickerDisplaySegments && id?.startsWith(SEGMENT_IN_GROUP_PICKER_IDENTIFIER)) {
                    return (
                        <SelectTextField.Option
                            {...dataIds}
                            after={<SegmentMoreInfo scope={scope} group={group} />}
                            {...additionalOptionProps}
                        />
                    );
                }

                return (
                    <SelectTextField.Option
                        {...dataIds}
                        after={group.uid ? <GroupMoreInfo scope={scope} group={group} /> : null}
                        {...additionalOptionProps}
                    />
                );
            }}
            onSearch={setSearchValue}
            isDisabled={selectTextFieldProps?.isDisabled || isDisabled}
            onLoadMore={fetchNextPage}
            onBlur={() => {
                setSearchValue('');
                setEnabled(false);
            }}
            options={groups}
            status={status}
            onFocus={() => setEnabled(true)}
        />
    );
};
